import { useEffect, useMemo, useState } from 'react'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ClipboardList, Loader2 } from 'lucide-react'

import { ResourceMetaLine } from '@modules/component/ResourceMetaLine/ResourceMetaLine'
import { Button } from '@modules/shadcn/ui/Button/Button'
import { Input } from '@modules/shadcn/ui/Input/Input'

import { ProcessImageType } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import type { GetDealWithColorSimulationResponse } from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'

interface SimulationDetailHeaderProps {
  data: GetDealWithColorSimulationResponse
  dealNameMutation?: {
    mutateAsync: (dealName: string) => Promise<unknown>
    isPending: boolean
  }
  surveyReportUrl?: string
  /** `surveyReportUrl` が無いときに、報告書ボタンを押したら呼ぶ（案件の選択・作成ダイアログを開く） */
  onSurveyReportClick?: () => void
  /** 選択中のシミュレーション結果の作成者名 */
  selectedCreatorName?: string
}

export const SimulationDetailHeader: React.FC<SimulationDetailHeaderProps> = ({
  data,
  dealNameMutation,
  surveyReportUrl,
  onSurveyReportClick,
  selectedCreatorName,
}) => {
  const initialDealName = data.dealName ?? ''
  const [dealNameInput, setDealNameInput] = useState(initialDealName)
  const [lastSavedDealName, setLastSavedDealName] = useState(initialDealName)

  // 最終更新日時を計算（表示対象の画像のuploadedAtの中で最新のもの）
  // CLEANED（グレースケール）画像は除外
  const lastUpdatedAt = useMemo(() => {
    const dates: Date[] = []

    data.images?.forEach(img => {
      if (img.uploadedAt) {
        dates.push(timestampDate(img.uploadedAt))
      }
      img.simulatedImage?.forEach(simImg => {
        // CLEANED 画像の更新日時は除外
        if (simImg.uploadedAt && simImg.type !== ProcessImageType.CLEANED) {
          dates.push(timestampDate(simImg.uploadedAt))
        }
      })
    })

    if (dates.length === 0) {
      return undefined
    }

    return dates.reduce((latest, current) => (current > latest ? current : latest))
  }, [data.images])

  useEffect(() => {
    const updatedDealName = data.dealName ?? ''
    setDealNameInput(updatedDealName)
    setLastSavedDealName(updatedDealName)
  }, [data.dealName])

  const handleDealNameBlur = async () => {
    if (dealNameMutation?.isPending) {
      return
    }

    if (dealNameInput === lastSavedDealName) {
      return
    }

    if (!dealNameMutation) {
      setLastSavedDealName(dealNameInput)
      return
    }

    try {
      await dealNameMutation.mutateAsync(dealNameInput)
      setLastSavedDealName(dealNameInput)
    } catch (error) {
      console.error('案件名の更新に失敗しました', error)
      setDealNameInput(lastSavedDealName)
    }
  }
  if (!dealNameMutation) {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Input
            aria-label="案件名"
            className="w-full"
            disabled={dealNameMutation.isPending}
            placeholder="案件名を入力"
            value={dealNameInput}
            onBlur={handleDealNameBlur}
            onChange={event => setDealNameInput(event.target.value)}
          />
          {dealNameMutation.isPending ? (
            <Loader2
              aria-hidden
              className="absolute w-4 h-4 right-4 top-1/2 -translate-y-1/2 animate-spin text-slate-400"
            />
          ) : null}
        </div>
        {surveyReportUrl ? (
          <Button asChild variant="outline" className="shrink-0 shadow-sm">
            <a href={surveyReportUrl} target="_blank" rel="noopener noreferrer">
              <ClipboardList className="mr-2" size={16} />
              現地調査報告書
            </a>
          </Button>
        ) : onSurveyReportClick ? (
          <Button
            type="button"
            variant="outline"
            className="shrink-0 shadow-sm"
            onClick={onSurveyReportClick}
          >
            <ClipboardList className="mr-2" size={16} />
            現地調査報告書
          </Button>
        ) : null}
      </div>
      <ResourceMetaLine
        authorName={selectedCreatorName || data.staffName}
        updatedAt={lastUpdatedAt}
      />
    </div>
  )
}
